import React, { useState, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { InputSwitch } from 'primereact/inputswitch';
import { Password } from 'primereact/password';
import Loader from "../../Shared/Loader/Loader";
import APIConstant from "../../../utils/pathConstants";
import api from "../../../utils/apiClient";
import CommonConfig from '../../../utils/constant';
import Toast from '../../Shared/Toast/Toast';
import { useCatchResponse } from '../../../utils/useCatchResponse';

const PaymentConfig = () => {
    const { catchResponse } = useCatchResponse();
    const [securityUserID, setSecurityUserID] = useState('');
    const [schoolId, setSchoolId] = useState('');
    const [razorpayKeyId, setRazorpayKeyId] = useState('');
    const [razorpayKeySecret, setRazorpayKeySecret] = useState('');
    const [hasExistingSecret, setHasExistingSecret] = useState(false);
    const [isOnlineEnabled, setIsOnlineEnabled] = useState(false);
    const [isManualEnabled, setIsManualEnabled] = useState(true);
    const [keyIdErr, setKeyIdErr] = useState('');
    const [keySecretErr, setKeySecretErr] = useState('');

    useEffect(() => {
        const loginData = CommonConfig.loginData();
        const userId = loginData?._id;
        const schoolid = loginData?.CreatedBy || loginData?._id;
        setSecurityUserID(userId);
        setSchoolId(schoolid);
        fetchConfig(schoolid);
    }, []);

    const fetchConfig = async (schoolid) => {
        try {
            Loader.show();
            const response = await api.post(APIConstant.path.GetPaymentConfig, { SchoolId: schoolid });
            if (response.success && response.data) {
                setRazorpayKeyId(response.data.RazorpayKeyId || '');
                setHasExistingSecret(response.data.HasRazorpaySecret || false);
                setIsOnlineEnabled(response.data.IsOnlinePaymentEnabled || false);
                setIsManualEnabled(response.data.IsManualPaymentEnabled ?? true);
            }
            Loader.hide();
        } catch (err) {
            Loader.hide();
            catchResponse(err);
        }
    };

    const saveConfig = async () => {
        let isValid = true;
        setKeyIdErr('');
        setKeySecretErr('');

        if (isOnlineEnabled && !razorpayKeyId) {
            setKeyIdErr('Razorpay Key ID is required for online payment');
            isValid = false;
        }
        if (isOnlineEnabled && !razorpayKeySecret && !hasExistingSecret) {
            setKeySecretErr('Razorpay Key Secret is required for online payment');
            isValid = false;
        }
        if (!isValid) return;

        try {
            Loader.show();
            const data = {
                SchoolId: schoolId,
                RazorpayKeyId: razorpayKeyId,
                IsOnlinePaymentEnabled: isOnlineEnabled,
                IsManualPaymentEnabled: isManualEnabled,
                CreatedBy: securityUserID,
            };
            if (razorpayKeySecret) {
                data.RazorpayKeySecret = razorpayKeySecret;
            }

            const response = await api.post(APIConstant.path.AddPaymentConfig, data);
            if (response.success) {
                Toast.success({ message: response.message });
                setRazorpayKeySecret('');
                setHasExistingSecret(true);
            } else {
                Toast.error({ message: response.message });
            }
            Loader.hide();
        } catch (err) {
            Loader.hide();
            catchResponse(err);
        }
    };

    return (
        <div className='wayment-owner-dashboard et-master-page'>
            <div className="heading-area p-d-flex p-ai-center p-jc-between">
                <div className="p-d-flex p-ai-baseline">
                    <i className="pi pi-cog"></i>
                    <h2>Payment Configuration</h2>
                </div>
            </div>

            <div className="p-grid p-py-3">
                <div className="p-col-12">
                    <div style={{
                        padding: '24px',
                        background: '#ffffff',
                        borderRadius: '12px',
                        boxShadow: '0 2px 12px rgba(28, 81, 94, 0.08)',
                        border: '1px solid #e8eef5',
                        transition: 'all 0.3s ease'
                    }}>
                        <h4 style={{ margin: '0 0 10px', color: '#1C515E', fontSize: '16px', fontWeight: '700', letterSpacing: '0.3px' }}>Payment Methods</h4>

                        <div className="p-d-flex p-ai-center p-jc-between p-mb-4" style={{
                            padding: '16px',
                            background: 'linear-gradient(135deg, #f0f7ff 0%, #f9fbff 100%)',
                            borderRadius: '10px',
                            border: '1px solid #e0eaff',
                            transition: 'all 0.3s ease'
                        }}>
                            <div>
                                <label style={{ fontWeight: '700', fontSize: '14px', color: '#1C515E', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <i className="pi pi-wallet" style={{ fontSize: '16px', color: '#5b6ddf' }}></i>
                                    Manual Payment
                                </label>
                                <p style={{ margin: '6px 0 0', color: '#666', fontSize: '12px', lineHeight: '1.5' }}>Parents upload payment proof, admin approves</p>
                            </div>
                            <InputSwitch checked={isManualEnabled} onChange={(e) => setIsManualEnabled(e.value)} style={{ transform: 'scale(1.1)' }} />
                        </div>

                        <div className="p-d-flex p-ai-center p-jc-between" style={{
                            padding: '16px',
                            background: 'linear-gradient(135deg, #fff0f5 0%, #fff9fb 100%)',
                            borderRadius: '10px',
                            border: '1px solid #ffe0ed',
                            transition: 'all 0.3s ease'
                        }}>
                            <div>
                                <label style={{ fontWeight: '700', fontSize: '14px', color: '#1C515E', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <i className="pi pi-credit-card" style={{ fontSize: '16px', color: '#e85d75' }}></i>
                                    Online Payment (Razorpay)
                                </label>
                                <p style={{ margin: '6px 0 0', color: '#666', fontSize: '12px', lineHeight: '1.5' }}>Parents pay online via Razorpay gateway</p>
                            </div>
                            <InputSwitch checked={isOnlineEnabled} onChange={(e) => { setIsOnlineEnabled(e.value); if (!e.value) { setKeyIdErr(''); setKeySecretErr(''); } }} style={{ transform: 'scale(1.1)' }} />
                        </div>
                    </div>
                </div>

                {isOnlineEnabled && (
                    <div className="p-col-12">
                        <div style={{
                            padding: '14px',
                            background: '#ffffff',
                            borderRadius: '12px',
                            boxShadow: '0 2px 12px rgba(232, 93, 117, 0.1)',
                            border: '1px solid #ffe0ed',
                            transition: 'all 0.3s ease'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                <i className="pi pi-key" style={{ fontSize: '18px', color: '#e85d75', fontWeight: 'bold' }}></i>
                                <h4 style={{ margin: 0, color: '#1C515E', fontSize: '16px', fontWeight: '700' }}>Razorpay Credentials</h4>
                            </div>
                            <div className="p-grid">
                                <div className="p-col-6">
                                    <label style={{ fontWeight: '700', fontSize: '13px', color: '#1C515E', marginBottom: '8px', display: 'block' }}>
                                        Key ID <span style={{ color: '#e85d75' }}>*</span>
                                    </label>
                                    <InputText
                                        className={`p-w-100 ${keyIdErr ? 'p-invalid' : ''}`}
                                        value={razorpayKeyId}
                                        onChange={(e) => { setRazorpayKeyId(e.target.value); setKeyIdErr(''); }}
                                        placeholder="rzp_live_xxxxx or rzp_test_xxxxx"
                                        style={{
                                            borderRadius: '8px',
                                            padding: '10px 12px',
                                            fontSize: '13px',
                                            border: '1px solid #e0eaff',
                                            transition: 'all 0.3s ease'
                                        }}
                                    />
                                    {keyIdErr && <small style={{ color: '#e85d75', display: 'block', marginTop: '6px', fontWeight: '500' }}>{keyIdErr}</small>}
                                </div>
                                <div className="p-col-6">
                                    <label style={{ fontWeight: '700', fontSize: '13px', color: '#1C515E', marginBottom: '8px', display: 'block' }}>
                                        Key Secret {hasExistingSecret ? <span style={{ color: '#5b6ddf', fontWeight: '600' }}>(saved)</span> : <span style={{ color: '#e85d75' }}>*</span>}
                                    </label>
                                    <Password
                                        className={`p-w-100 ${keySecretErr ? 'p-invalid' : ''}`}
                                        value={razorpayKeySecret}
                                        onChange={(e) => { setRazorpayKeySecret(e.target.value); setKeySecretErr(''); }}
                                        placeholder={hasExistingSecret ? 'Leave blank to keep existing' : 'Enter Key Secret'}
                                        feedback={false}
                                        toggleMask
                                        style={{
                                            width: '100%',
                                            display: 'block'
                                        }}
                                        inputStyle={{
                                            width: '100%',
                                            borderRadius: '8px',
                                            padding: '10px 12px',
                                            fontSize: '13px',
                                            border: '1px solid #e0eaff',
                                            transition: 'all 0.3s ease',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                    {keySecretErr && <small style={{ color: '#e85d75', display: 'block', marginTop: '6px', fontWeight: '500' }}>{keySecretErr}</small>}
                                    {!keySecretErr && hasExistingSecret && !razorpayKeySecret && (
                                        <small style={{ color: '#5b6ddf', display: 'block', marginTop: '6px', fontWeight: '500' }}>✓ Secret is saved. Enter new value to update.</small>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div className="dialog-footer p-d-flex p-ai-center p-jc-end p-mx-4">
                <Button
                    label="Save Configuration"
                    icon="pi pi-save"
                    className="btn-text-action-primary"
                    onClick={saveConfig}
                />
            </div>
        </div>
    );
};

export default PaymentConfig;
